// Capa HTTP del Mantenedor de IA. Tipos + funciones fetch; el estado vive en
// TanStack Query (ver `src/hooks/useMantenedorDeIa.ts`).

import { apiFetch } from "./api";
import type { IaPrecio } from "./consumoDeIa";

/**
 * Qué clase de trabajo hace una función.
 *
 * `asistencia` reescribe lo que el usuario ya escribió; `generacion` produce
 * contenido nuevo desde otras fuentes. La pantalla las separa porque no se
 * configuran con el mismo criterio: una asistencia quiere temperatura baja y
 * prompt genérico, una generación quiere prompt de dominio.
 */
export type CategoriaIaFuncion = "asistencia" | "generacion";

export interface UsoDeIaFuncion {
  corridas: number;
  aceptadas: number;
  descartadas: number;
  tokens_entrada: number;
  tokens_salida: number;
}

/** El contexto propio que una función le declara a un campo. */
export interface ContextoDeCampo {
  id: number;
  campo: string;
  instruccion: string;
}

export interface IaFuncion {
  id: number;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  /** Nula en las asistencias: no viven en una pantalla, viven en muchas. */
  pantalla: string | null;
  categoria: CategoriaIaFuncion;
  instruccion: string;
  proveedor: string;
  modelo: string;
  temperatura: number | null;
  /** Indexadas por proveedor porque su forma cambia con él (ahí va "thinking"). */
  opciones_proveedor: Record<string, unknown> | null;
  /**
   * Si la función suma un contexto propio por campo al prompt base.
   *
   * LO DECLARA EL SERVIDOR (`IaFuncion::usaContextoPorCampo()`) y la pantalla
   * no lo deduce, aunque hoy `categoria === "asistencia"` daría lo mismo. Lo
   * que decide el hecho es quién llama a `instruccionPara()`, que vive allá;
   * deducirlo acá serían dos declaraciones que nada obliga a coincidir.
   */
  usa_contexto_por_campo: boolean;
  campos: ContextoDeCampo[];
  uso?: UsoDeIaFuncion;
}

/** Un campo del producto con asistencia declarada. */
export interface IaCampo {
  id: number;
  codigo: string;
  nombre: string;
  pantalla: string;
}

export interface IaFuncionInput {
  instruccion: string;
  proveedor: string;
  modelo: string;
  temperatura: number | null;
  opciones_proveedor: Record<string, unknown> | null;
}

export async function fetchIaFunciones(): Promise<IaFuncion[]> {
  const { data } = await apiFetch<{ data: IaFuncion[] }>("/api/ia/funciones");
  return data;
}

export async function fetchIaCampos(): Promise<IaCampo[]> {
  const { data } = await apiFetch<{ data: IaCampo[] }>("/api/ia/campos");
  return data;
}

/** La función guardada y, si el prompt quedó como borrador, la marca que el servidor trae aparte. */
export type IaFuncionGuardada = IaFuncion & { borrador_guardado?: boolean };

export async function guardarIaFuncionRequest(
  id: number,
  input: IaFuncionInput,
): Promise<IaFuncionGuardada> {
  const { data, borrador_guardado } = await apiFetch<{ data: IaFuncion; borrador_guardado?: boolean }>(
    `/api/ia/funciones/${id}`,
    { method: "PUT", body: JSON.stringify(input) },
  );
  return { ...data, borrador_guardado: borrador_guardado === true };
}

export async function guardarContextoDeCampoRequest(
  funcionId: number,
  campo: string,
  instruccion: string,
): Promise<IaFuncion> {
  const { data } = await apiFetch<{ data: IaFuncion }>(`/api/ia/funciones/${funcionId}/campos`, {
    method: "PUT",
    body: JSON.stringify({ campo, instruccion }),
  });
  return data;
}

/**
 * Quita el contexto propio de un campo.
 *
 * No es destructivo aunque el verbo lo parezca: el campo vuelve al prompt
 * base, que es el estado normal de la mayoría.
 */
export function olvidarContextoDeCampoRequest(funcionId: number, campo: string): Promise<void> {
  return apiFetch<void>(
    `/api/ia/funciones/${funcionId}/campos/${encodeURIComponent(campo)}`,
    { method: "DELETE" },
  );
}

/**
 * Los modelos que ofrece el selector: los del catálogo de precios para el
 * proveedor elegido, sin repetir (un modelo tiene una fila por vigencia) y
 * ordenados. El modelo guardado se suma siempre, aunque el catálogo no lo
 * traiga o todavía no haya cargado: sin él, editar solo el prompt de una
 * función cuyo modelo se retiró dejaría el selector sin valor. Con otro
 * proveedor elegido el guardado es de OTRO catálogo, así que se pasa `null`:
 * ofrecerlo invitaría a guardar un modelo que el servidor rechaza.
 */
export function modelosOfrecidos(
  precios: readonly IaPrecio[] | undefined,
  proveedor: string,
  modeloGuardado: string | null,
): string[] {
  const modelos = new Set(
    (precios ?? []).filter((p) => p.proveedor === proveedor).map((p) => p.modelo),
  );
  if (modeloGuardado !== null) modelos.add(modeloGuardado);
  return [...modelos].sort();
}
